/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計タブの子票行モデル。初期セット（Detailed Design 〜 Release の 11 行）の生成と、行の追加 / 複製 / 削除 / 並べ替え / 合計 pt の算出を、描画から切り離した純関数で持つ
 * @graph-connects none
 */
import { defaultAssigneeFor, findChildTicketKind, } from './basic-design-kinds.js';
/** 行 id の連番。描画のたびに変わらないよう module scope に置く。 @graph-connects none */
let rowSeq = 0;
/**
 * 行 id を 1 つ払い出す。
 * @graph-connects none
 */
export function nextChildTicketRowId() {
    rowSeq += 1;
    return `child-ticket-row-${String(rowSeq)}`;
}
/**
 * 初期状態で並べる子票種別。案件を切ったときに必ず要る 13 枚を、着手順に近い並びで置く。
 * @graph-connects none
 */
export const INITIAL_CHILD_TICKET_KIND_IDS = [
    'basic-design',
    'basic-design-review',
    'detailed-design',
    'detailed-design-review',
    'it-creation',
    'it-creation-review',
    'develop',
    'code-review',
    'release-procedure-creation',
    'release-procedure-review',
    'it-execution',
    'uat-execution',
    'release',
];
/**
 * 種別だけ決まった空行を作る。
 * @graph-connects nextChildTicketRowId [calls] 行 id の採番
 */
export function createChildTicketRow(kindId, people) {
    const kind = findChildTicketKind(kindId);
    return {
        rowId: nextChildTicketRowId(),
        kindId,
        item: null,
        repository: null,
        content: '',
        point: '',
        // 誰がやるか決まっている工程（基本設計 / 詳細設計 / 開発 / リリース手順書作成 =
        // 開発リーダー、各レビュー = DLE、総合テスト実施 = PL）は最初から入れておく。
        // 毎回同じ人を手で選び直すことになるため。
        assignee: kind === undefined || people === undefined ? null : defaultAssigneeFor(kind, people),
    };
}
/**
 * 保存済みの行を読み込むときに、採番がそれらとぶつからない位置まで進める。
 *
 * 行 id は展開の差分の突き合わせ鍵なので、読み込んだ行の id は**そのまま使う**。
 * 一方で採番は読み込み直後に 1 から数え直すため、履歴から戻した行を編集して
 * 行を足すと同じ id を払い出しうる。そうなると 1 枚の子票に 2 行が畳まれて
 * pt だけ二重に数えられるので、読み込んだ時点で採番を追い越させる。
 * @graph-connects none
 */
export function reserveChildTicketRowIds(rows) {
    for (const row of rows) {
        const found = /^child-ticket-row-(\d+)$/.exec(row.rowId);
        if (found === null)
            continue;
        rowSeq = Math.max(rowSeq, Number(found[1]));
    }
}
/**
 * 初期セットの行を作る。
 * @graph-connects createChildTicketRow [calls] 種別ごとの空行生成
 */
export function createInitialChildTicketRows(people) {
    return INITIAL_CHILD_TICKET_KIND_IDS.map((kindId) => createChildTicketRow(kindId, people));
}
/**
 * 行を複製して元の行の直後に差し込む。pt や担当者ごと複製するので、
 * 同じ作業を複数リポジトリへ割るときに 1 行目だけ書けば済む。
 * @graph-connects nextChildTicketRowId [calls] 複製行の id 採番
 */
export function duplicateChildTicketRow(rows, rowId) {
    const index = rows.findIndex((row) => row.rowId === rowId);
    const source = rows[index];
    if (source === undefined)
        return [...rows];
    const next = [...rows];
    next.splice(index + 1, 0, { ...source, rowId: nextChildTicketRowId() });
    return next;
}
/**
 * `index` の位置へ新しい行を差し込む。`index` が行数と等しいときは末尾に足す。
 * 行間の「＋」と表末尾の「行を追加」が同じ経路を通る。
 * @graph-connects createChildTicketRow [calls] 差し込む空行の生成
 */
export function insertChildTicketRow(rows, index, kindId) {
    const at = Math.min(Math.max(index, 0), rows.length);
    const next = [...rows];
    next.splice(at, 0, createChildTicketRow(kindId));
    return next;
}
/**
 * 行を取り除く。
 * @graph-connects none
 */
export function removeChildTicketRow(rows, rowId) {
    return rows.filter((row) => row.rowId !== rowId);
}
/**
 * `from` 番目の行を `to` 番目へ動かす。範囲外・移動なしは元の並びをそのまま返す。
 * @graph-connects none
 */
export function moveChildTicketRow(rows, from, to) {
    if (from === to)
        return [...rows];
    if (from < 0 || from >= rows.length)
        return [...rows];
    if (to < 0 || to >= rows.length)
        return [...rows];
    const next = [...rows];
    const [moved] = next.splice(from, 1);
    if (moved === undefined)
        return [...rows];
    next.splice(to, 0, moved);
    return next;
}
/**
 * 合計 pt。数値として読めない pt（空・途中入力）は 0 として足す。
 * @graph-connects none
 */
export function totalChildTicketPoint(rows) {
    return rows.reduce((sum, row) => {
        const value = Number.parseFloat(row.point);
        return Number.isFinite(value) ? sum + value : sum;
    }, 0);
}
//# sourceMappingURL=basic-design-rows.js.map